import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useAuth, resendLoginCode, type LoginChallenge } from "@/lib/auth";
import { useT } from "@/lib/i18n";

/** Seconds before "send a new code" becomes available again. */
const RESEND_COOLDOWN = 30;

export function Login() {
  const navigate = useNavigate();
  const auth = useAuth();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Second factor: set once the password is accepted and a code is on its way.
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  if (auth.status === "authenticated") {
    navigate("/", { replace: true });
  }

  // Focus the code box the moment it appears, so the flow is type-tab-type.
  useEffect(() => {
    if (challenge) codeInputRef.current?.focus();
  }, [challenge]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  async function onSubmitPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await auth.signIn(email.trim().toLowerCase(), password);
      setChallenge(res);
      setCooldown(RESEND_COOLDOWN);
      // Staging/local have no mailbox — the API hands the code back instead.
      if (res.devCode) {
        setCode(res.devCode);
        toast.info(`Kods (tikai ne-produkcijā): ${res.devCode}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_credentials") {
        toast.error(t("auth.invalidCredentials", "Nepareizs e-pasts vai parole"));
      } else if (err instanceof ApiError && err.code === "too_many_codes") {
        toast.error(
          t("auth.tooManyCodes", "Pārāk daudz mēģinājumu. Mēģiniet vēlāk."),
        );
      } else if (err instanceof ApiError && err.code === "code_send_failed") {
        toast.error(
          t("auth.codeSendFailed", "Neizdevās nosūtīt kodu. Mēģiniet vēlreiz."),
        );
      } else {
        toast.error(t("auth.loginFailed", "Pieslēgšanās neizdevās"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || !challenge) return;
    setSubmitting(true);
    try {
      await auth.completeSignIn(challenge.challengeId, code.trim().toUpperCase());
      navigate("/", { replace: true });
    } catch (err) {
      const codeErr = err instanceof ApiError ? err.code : null;
      if (codeErr === "code_expired") {
        toast.error(
          t("auth.codeExpired", "Kodam beidzies derīguma termiņš. Pieprasiet jaunu."),
        );
      } else if (codeErr === "too_many_attempts") {
        toast.error(
          t("auth.tooManyAttempts", "Pārāk daudz nepareizu mēģinājumu. Pieprasiet jaunu kodu."),
        );
      } else {
        toast.error(t("auth.invalidCode", "Nepareizs kods"));
      }
      setCode("");
      codeInputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (!challenge || cooldown > 0) return;
    try {
      const res = await resendLoginCode(challenge.challengeId);
      setCooldown(RESEND_COOLDOWN);
      if (res.devCode) {
        setCode(res.devCode);
        toast.info(`Kods (tikai ne-produkcijā): ${res.devCode}`);
      } else {
        toast.success(t("auth.codeResent", "Jauns kods nosūtīts"));
      }
    } catch {
      toast.error(t("auth.codeSendFailed", "Neizdevās nosūtīt kodu"));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="font-display">
            {t("auth.signInTitle", "Vīnoga Admin")}
          </CardTitle>
          <CardDescription>
            {challenge
              ? t("auth.codePrompt", "Ievadiet kodu, ko nosūtījām uz") +
                ` ${challenge.email}`
              : t("auth.signInPrompt", "Pieslēdzieties, lai turpinātu.")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!challenge ? (
            <form className="space-y-4" onSubmit={onSubmitPassword}>
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email", "E-pasts")}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="you@vinoga.lv"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password", "Parole")}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !email || !password}
              >
                {submitting
                  ? t("auth.sendingCode", "Sūta kodu…")
                  : t("auth.signIn", "Pieslēgties")}
              </Button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={onSubmitCode}>
              <div className="space-y-2">
                <Label htmlFor="code">{t("auth.code", "Pieslēgšanās kods")}</Label>
                <Input
                  id="code"
                  ref={codeInputRef}
                  // one-time-code lets iOS/Android offer the emailed code.
                  autoComplete="one-time-code"
                  inputMode="text"
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={6}
                  required
                  placeholder="A1B2C3"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  disabled={submitting}
                  className="text-center font-mono text-lg tracking-[0.4em]"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || code.trim().length !== 6}
              >
                {submitting
                  ? t("auth.verifying", "Pārbauda…")
                  : t("auth.verify", "Apstiprināt")}
              </Button>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={onResend}
                  disabled={cooldown > 0}
                  className="font-body text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                >
                  {cooldown > 0
                    ? `${t("auth.resendIn", "Jaunu kodu pēc")} ${cooldown}s`
                    : t("auth.resend", "Nosūtīt jaunu kodu")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChallenge(null);
                    setCode("");
                    setPassword("");
                  }}
                  className="font-body text-xs text-muted-foreground hover:text-foreground"
                >
                  {t("auth.back", "Atpakaļ")}
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
